import { jsx as _jsx } from "react/jsx-runtime";
import { useReducedMotion } from '#pplx/frontend/common/hooks/useReducedMotion.js';
import { ComposerContainerContext } from '#pplx/frontend/composer-sdk/ComposerContainerContext.js';
import { COMPOSER_MOTION_SPRING } from '#pplx/frontend/composer-sdk/composerMotion.js';
import { animated, useSpring, useTransition } from '@react-spring/web';
import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
const VISIBLE = { opacity: 1, filter: 'blur(0px)', transform: 'scale(1)' };
/**
 * Transitions decorative label/icon content inside one stable composer button.
 * The parent button must supply its current accessible label. Children must not
 * contain interactive elements; outgoing content is hidden from assistive technology.
 * Unframed composers render children directly. The key identifies visible content,
 * including localized labels, rather than the action's stable identity.
 */
export function ComposerLabelTransition({ children, contentKey, }) {
    const { isFramed } = useContext(ComposerContainerContext);
    return isFramed ? _jsx(AnimatedLabel, { contentKey: contentKey, children: children }) : children;
}
function AnimatedLabel({ children, contentKey, }) {
    const reducedMotion = useReducedMotion();
    const contentRef = useRef(null);
    const [width, setWidth] = useState();
    const hasMeasured = useRef(false);
    useLayoutEffect(() => {
        const element = contentRef.current;
        if (!element)
            return;
        // Layout width excludes the scale applied during the text transition.
        const measure = () => setWidth(element.offsetWidth);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [contentKey]);
    const size = useSpring({
        width: width ?? 0,
        config: COMPOSER_MOTION_SPRING,
        immediate: reducedMotion || !hasMeasured.current,
    });
    useEffect(() => {
        if (width !== undefined)
            hasMeasured.current = true;
    }, [width]);
    const item = useMemo(() => ({ contentKey, children }), [contentKey, children]);
    const transitions = useTransition(item, {
        keys: (value) => value.contentKey,
        initial: VISIBLE,
        from: { opacity: 0, filter: 'blur(6px)', transform: 'scale(0.98)' },
        enter: VISIBLE,
        leave: { opacity: 0, filter: 'blur(6px)', transform: 'scale(1.02)' },
        config: COMPOSER_MOTION_SPRING,
        immediate: reducedMotion,
    });
    return (_jsx(animated.span, { "aria-hidden": true, 
        // Preserve the text baseline so Button's trimmed label box stays centered with its icons.
        className: "pointer-events-none relative inline-flex justify-end align-baseline", style: { width: width === undefined ? undefined : size.width }, children: transitions((style, value) => (_jsx(animated.span, { ref: value.contentKey === contentKey ? contentRef : undefined, className: value.contentKey === contentKey
                ? 'inline-flex w-max shrink-0 origin-right items-center rtl:origin-left'
                : 'absolute end-0 top-0 inline-flex w-max origin-right items-center rtl:origin-left', style: style, children: value.children }))) }));
}
//# sourceMappingURL=ComposerLabelTransition.js.map